import React, { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  ArrowUpRight,
  Building2,
  CheckCircle2,
  Globe2,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  Sparkles,
} from "lucide-react";

import Head from "../Components/Layout/Head";
import FooterNew from "../Components/Layout/FooterNew";
import ApiPath from "../Components/Common/Apiurl";
import { CountryContext } from "../CountryContext";
import { OFFICE_LOCATIONS } from "../Config/officeLocations";

const COUNTRY_OPTIONS = [
  "Australia",
  "Germany",
  "India",
  "New Zealand",
  "United States",
  "Other",
];

const CONTACT_REASONS = [
  "AI Automation",
  "Manufacturing AI",
  "Legal AI",
  "Healthcare AI",
  "Custom Software / AI Development",
  "Partnership / Other",
];

const getCountryCode = (country) => {
  const map = {
    India: "IN",
    "United States": "US",
    Australia: "AU",
    Germany: "DE",
    "New Zealand": "NZ",
  };

  return map[country] || "US";
};

const ContactUsAI = () => {
  const detectedCountry = useContext(CountryContext);
  const websiteURL = window.location.origin;

  const initialCountry = useMemo(
    () =>
      COUNTRY_OPTIONS.includes(detectedCountry)
        ? detectedCountry
        : "United States",
    [detectedCountry],
  );

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: initialCountry,
    lookingFor: "",
    message: "",
  });
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    AOS.init({
      duration: 850,
      easing: "ease-out-cubic",
      once: true,
      offset: 70,
    });
  }, []);

  useEffect(() => {
    if (COUNTRY_OPTIONS.includes(detectedCountry)) {
      setFormData((current) => ({
        ...current,
        country: detectedCountry,
      }));
    }
  }, [detectedCountry]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    setErrors((current) => ({
      ...current,
      [name]: "",
    }));

    if (name === "country") {
      setPhone("");
      setErrors((current) => ({
        ...current,
        phone: "",
        country: "",
      }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailRegex = /^\S+@\S+\.\S+$/;

    if (!formData.name.trim()) {
      nextErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!phone) {
      nextErrors.phone = "Phone number is required";
    } else if (!isValidPhoneNumber(phone)) {
      nextErrors.phone = "Enter a valid phone number";
    }

    if (!formData.country) {
      nextErrors.country = "Country is required";
    }

    if (!formData.lookingFor) {
      nextErrors.lookingFor = "Please select what you are looking for";
    }
    if (formData.message.length > 220) {
      nextErrors.message = "Please leave short message";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!validateForm()) return;

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone,
      lookingFor: formData.lookingFor,
      queryPageIndex: "CONTACT_US_AI",
      message:
        `${formData.message || ""}` +
        ` | IP Country: ${detectedCountry || "Unknown"}` +
        ` | Selected Country: ${formData.country}` +
        ` | URL: ${websiteURL}/contact-us`,
    };

    try {
      setLoading(true);

      const response = await fetch(
        ApiPath + "/api/enquiryController/tqAiEnquiry",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`Enquiry API returned ${response.status}`);
      }

      if (window.fbq) {
        window.fbq("track", "Lead");
      }

      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        country: initialCountry,
        lookingFor: "",
        message: "",
      });
      setPhone("");
      setErrors({});
    } catch (error) {
      console.error("Contact enquiry submission failed", error);
      setFormError(
        "We could not submit your enquiry right now. Please email enquiry@thinkquotient.ai.",
      );
    } finally {
      setLoading(false);
    }
  };

  const isIndiaVisitor = detectedCountry === "India";

  const offices = [
    {
      ...OFFICE_LOCATIONS.usa,
      icon: Globe2,
      eyebrow: "United States",
      description:
        "Connect with our US office for AI automation, enterprise solutions, partnerships, and project discussions.",
      highlighted: !isIndiaVisitor,
    },
    {
      ...OFFICE_LOCATIONS.india,
      icon: Building2,
      eyebrow: "India",
      description:
        "Our India branch supports AI delivery, software engineering, implementation, and customer success.",
      highlighted: isIndiaVisitor,
    },
  ];

  const pageTitle = "Contact ThinkQuotient AI | USA Office & India Branch";
  const metaDescription =
    "Contact ThinkQuotient AI for AI automation and custom software solutions. Find our USA office in Rockville, Maryland and India branch in Pune, Maharashtra.";
  const canonicalUrl = "https://thinkquotient.ai/contact-us";

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ThinkQuotient Software Pvt. Ltd.",
    alternateName: "ThinkQuotient AI",
    url: "https://thinkquotient.ai/",
    email: "enquiry@thinkquotient.ai",
    location: [
      {
        "@type": "Place",
        name: "ThinkQuotient USA Office",
        address: {
          "@type": "PostalAddress",
          streetAddress: "3025 Windy Knoll Ct, Suite #100",
          addressLocality: "Rockville",
          addressRegion: "MD",
          postalCode: "20850",
          addressCountry: "US",
        },
      },
      {
        "@type": "Place",
        name: "ThinkQuotient India Branch",
        telephone: "+91-7030055128",
        address: {
          "@type": "PostalAddress",
          streetAddress:
            "Shree Chaitanya Apartment, A/09, Shiv Darshan Rd, near Muktangan English School & Junior College, Vrindavan Society, Sivadarshan Poorgrasta Vasahat, Parvati",
          addressLocality: "Pune",
          addressRegion: "Maharashtra",
          postalCode: "411009",
          addressCountry: "IN",
        },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <div className="overflow-hidden bg-white text-slate-800">
        <Head />

        {/* HERO */}
        <section
          className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-20 sm:py-24 lg:py-28"
          style={{
            backgroundImage: "url('/assets/images/freepik__expand__54122.png')",
          }}
        >
          <div className="absolute inset-0 bg-slate-950/10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/55 via-blue-950/25 to-transparent"></div>
          <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl"></div>
          <div className="absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl"></div>

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div
                data-aos="fade-up"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 backdrop-blur-md"
              >
                <Sparkles size={16} className="text-blue-300" />
                Contact ThinkQuotient AI
              </div>

              <h1
                data-aos="fade-up"
                data-aos-delay="100"
                className="mt-6 text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl"
              >
                Let&apos;s Discuss Where AI Can Create Real Business Impact
              </h1>

              <p
                data-aos="fade-up"
                data-aos-delay="200"
                className="mt-6 max-w-3xl text-base leading-8 text-white/80 sm:text-lg"
              >
                Tell us what you want to automate, improve, or build. Our team
                can help with AI automation, document intelligence, workflow
                systems, and custom software across the US and India.
              </p>
            </div>
          </div>
        </section>

        {/* OFFICE LOCATIONS */}
        <section className="relative py-16 sm:py-20 lg:py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white"></div>
          <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-blue-100/60 blur-3xl"></div>
          <div className="absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-indigo-100/60 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm">
                <MapPin size={16} />
                Our Locations
              </div>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                Global Presence, Local Collaboration
              </h2>
              <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">
                ThinkQuotient AI operates through our USA office and India
                branch. Both locations are available for project discussions and
                business enquiries.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-2">
              {offices.map((office, index) => {
                const OfficeIcon = office.icon;

                return (
                  <article
                    key={office.key}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    className={`relative overflow-hidden rounded-3xl border p-7 shadow-[0_18px_50px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.12)] sm:p-8 ${
                      office.highlighted
                        ? "border-blue-200 bg-gradient-to-br from-blue-50/80 via-white to-indigo-50/50"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="absolute right-0 top-0 h-40 w-40 translate-x-12 -translate-y-12 rounded-full bg-blue-100/60 blur-3xl"></div>

                    <div className="relative">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg">
                            <OfficeIcon size={25} />
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
                              {office.eyebrow}
                            </p>
                            <h3 className="mt-1 text-2xl font-bold text-slate-950">
                              {office.label}
                            </h3>
                          </div>
                        </div>

                        {office.highlighted && (
                          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
                            <CheckCircle2 size={14} />
                            Nearest office
                          </span>
                        )}
                      </div>

                      <p className="mt-6 text-sm leading-7 text-slate-600 sm:text-base">
                        {office.description}
                      </p>

                      <div className="mt-6 space-y-4 border-t border-slate-200/80 pt-6">
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                            <MapPin size={17} />
                          </span>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                              Address
                            </p>
                            <p className="mt-1 text-sm leading-7 text-slate-700">
                              {office.address}
                            </p>
                          </div>
                        </div>

                        {office.phone && (
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                              <Phone size={17} />
                            </span>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                                Phone
                              </p>
                              <a
                                href={office.phone.href}
                                className="mt-1 block text-sm font-semibold text-slate-800 transition hover:text-blue-600"
                              >
                                {office.phone.display}
                              </a>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                            <Mail size={17} />
                          </span>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                              Email
                            </p>
                            <a
                              href="mailto:enquiry@thinkquotient.ai"
                              className="mt-1 block break-all text-sm font-semibold text-slate-800 transition hover:text-blue-600"
                            >
                              enquiry@thinkquotient.ai
                            </a>
                          </div>
                        </div>
                      </div>

                      <a
                        href={office.mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="group mt-7 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-all hover:border-blue-200 hover:text-blue-700 hover:shadow-md"
                      >
                        View on Google Maps
                        <ArrowUpRight
                          size={16}
                          className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* CONTACT FORM */}
        <section className="relative overflow-hidden bg-slate-950 py-16 text-white sm:py-20 lg:py-24">
          <div className="absolute -left-24 top-10 h-80 w-80 rounded-full bg-blue-500/15 blur-3xl"></div>
          <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-indigo-500/15 blur-3xl"></div>

          <div className="relative mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div data-aos="fade-right" className="lg:pr-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-blue-200">
                <MessageSquare size={16} />
                Start a Conversation
              </div>

              <h2 className="mt-5 text-3xl font-bold tracking-tight sm:text-4xl">
                Tell Us About Your Business Requirement
              </h2>

              <p className="mt-5 max-w-xl text-base leading-8 text-slate-300">
                Share your current workflow, pain point, or automation idea. Our
                team will review it and connect with you to discuss the most
                practical next step.
              </p>

              <div className="mt-8 space-y-4 text-sm text-slate-300">
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="mt-0.5 shrink-0 text-blue-300"
                  />
                  AI automation and workflow consultation
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="mt-0.5 shrink-0 text-blue-300"
                  />
                  Industry-specific AI solution discussion
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="mt-0.5 shrink-0 text-blue-300"
                  />
                  Custom AI and software development requirements
                </div>
              </div>

              <a
                href="mailto:enquiry@thinkquotient.ai"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-white transition hover:text-blue-200"
              >
                <Mail size={17} />
                enquiry@thinkquotient.ai
              </a>
            </div>

            <div
              data-aos="fade-left"
              className="rounded-3xl border border-white/10 bg-white p-6 text-slate-800 shadow-[0_30px_90px_rgba(0,0,0,0.28)] sm:p-8"
            >
              {submitted ? (
                <div className="flex min-h-[430px] flex-col items-center justify-center text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <CheckCircle2 size={32} />
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-slate-950">
                    Thank You
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-7 text-slate-600">
                    Your enquiry has been submitted. Our team will review your
                    requirement and get back to you.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-6 rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        className="text-sm font-semibold text-slate-700"
                        htmlFor="contact-name"
                      >
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                        placeholder="Your name"
                      />
                      {errors.name && (
                        <p className="mt-1.5 text-xs text-red-600">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        className="text-sm font-semibold text-slate-700"
                        htmlFor="contact-email"
                      >
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                        placeholder="you@company.com"
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-xs text-red-600">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-slate-700">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2 rounded-xl border border-slate-200 bg-white px-4 py-3 transition focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-50">
                        <PhoneInput
                          key={formData.country}
                          international
                          defaultCountry={getCountryCode(formData.country)}
                          value={phone}
                          onChange={(value) => {
                            setPhone(value || "");
                            setErrors((current) => ({ ...current, phone: "" }));
                          }}
                          className="text-sm"
                        />
                      </div>
                      {errors.phone && (
                        <p className="mt-1.5 text-xs text-red-600">
                          {errors.phone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        className="text-sm font-semibold text-slate-700"
                        htmlFor="contact-country"
                      >
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="contact-country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                      >
                        {COUNTRY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {errors.country && (
                        <p className="mt-1.5 text-xs text-red-600">
                          {errors.country}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-5">
                    <label
                      className="text-sm font-semibold text-slate-700"
                      htmlFor="contact-looking-for"
                    >
                      I&apos;m interested in{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="contact-looking-for"
                      name="lookingFor"
                      value={formData.lookingFor}
                      onChange={handleChange}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                    >
                      <option value="">Select a requirement</option>
                      {CONTACT_REASONS.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                    {errors.lookingFor && (
                      <p className="mt-1.5 text-xs text-red-600">
                        {errors.lookingFor}
                      </p>
                    )}
                  </div>

                  <div className="mt-5">
                    <label
                      className="text-sm font-semibold text-slate-700"
                      htmlFor="contact-message"
                    >
                      Tell us about your requirement
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-2 w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                      placeholder="Briefly describe the process, challenge, or solution you want to discuss..."
                    ></textarea>
                    {errors.message && (
                      <p className="mt-1.5 text-xs text-red-600">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {formError && (
                    <div className="mt-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? "Submitting..." : "Submit Enquiry"}
                    {!loading && (
                      <Send
                        size={16}
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <FooterNew />
      </div>
    </>
  );
};

export default ContactUsAI;
